import React, { useEffect, useState } from 'react';
import { supabase } from '../services/supabase';
import { LogOut, LayoutDashboard, Menu, X, ShieldAlert, Settings, Activity, Brain, BarChart2, Zap, ChevronDown, PieChart, TrendingUp, History as HistoryIcon } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

// Monthly analysis quota per plan
const PLAN_QUOTA_LIMITS: Record<string, number> = {
  Pro: 70,
  Elite: 250,
};

export const Header: React.FC = () => {
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [signalsOpen, setSignalsOpen] = useState(false);

  // Desktop guest dropdowns
  const [desktopFeaturesOpen, setDesktopFeaturesOpen] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async (userId: string) => {
      const { data } = await supabase.from('user_profiles').select('monthly_checks_used, tier, role').eq('id', userId).single();
      if (data) setProfile(data);
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user?.id) fetchProfile(session.user.id);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session?.user?.id) fetchProfile(session.user.id);
      else setProfile(null);
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <header className="border-b border-gray-800 bg-[#060810]/80 backdrop-blur-xl sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between relative">

        {/* Logo (Left) */}
        <div className="flex-1 flex justify-start">
          <Link to="/" className="flex items-center">
            <img
              src="https://zhyekkoguoxhvryucmwk.supabase.co/storage/v1/object/public/website-assets/website_header%20only%20logo.png"
              alt="ForexFund"
              className="h-10 w-auto object-contain"
              fetchPriority="high"
            />
          </Link>
        </div>

        {/* ── GUEST DESKTOP NAVIGATION (Center & Right) ── */}
        {!session ? (
          <>
            {/* Center Section: Main Navigation Links */}
            <div className="hidden md:flex flex-1 justify-center items-center gap-12">
              {/* 1. Features Dropdown */}
              <div className="relative">
                <button
                  onMouseEnter={() => setDesktopFeaturesOpen(true)}
                  className="flex items-center gap-1.5 text-base font-bold text-gray-400 hover:text-white transition-colors py-2"
                >
                  Features
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${desktopFeaturesOpen ? 'rotate-180' : ''}`} />
                </button>

                {desktopFeaturesOpen && (
                  <div
                    onMouseLeave={() => setDesktopFeaturesOpen(false)}
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-[480px] bg-[#0d1220] border border-gray-800 rounded-2xl shadow-2xl p-6 animate-in fade-in zoom-in-95 duration-200 z-[60]"
                  >
                    <div className="grid grid-cols-2 gap-8">
                      {/* Column 1: Analysis */}
                      <div>
                        <p className="text-[11px] font-black text-finance-accent uppercase tracking-widest mb-4">Analysis Tools</p>
                        <div className="space-y-1">
                          <Link to="/features/fundamental" className="block p-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 font-bold transition-all">Fundamental Analysis</Link>
                          <Link to="/features/cot" className="block p-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 font-bold transition-all">COT Performance</Link>
                          <Link to="/features/order-flow" className="block p-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 font-bold transition-all">Order Flow Mapping</Link>
                          <Link to="/features/charts" className="block p-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 font-bold transition-all">Chart Insights</Link>
                        </div>
                      </div>
                      {/* Column 2: Signals */}
                      <div>
                        <p className="text-[11px] font-black text-emerald-400 uppercase tracking-widest mb-4">Trading Signals</p>
                        <div className="space-y-1">
                          <Link to="/features/standard-signals" className="block p-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 font-bold transition-all">Standard Signals</Link>
                          <Link to="/features/elite-signals" className="block p-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 font-bold transition-all">Elite Strategic Signals</Link>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* 2. Pricing */}
              <Link to="/pricing" className="text-base font-bold text-gray-400 hover:text-white transition-colors">Pricing</Link>

              {/* 3. Insights */}
              <Link to="/insights" className="text-base font-bold text-gray-400 hover:text-white transition-colors">Insights</Link>

              {/* 4. About */}
              <Link to="/about" className="text-base font-bold text-gray-400 hover:text-white transition-colors">About</Link>
            </div>

            {/* Right Section: Auth Buttons */}
            <div className="hidden md:flex flex-1 justify-end items-center gap-8">
              <Link to="/login" className="text-base font-bold text-gray-400 hover:text-white transition-colors">Sign In</Link>
              <Link to="/pricing" className="px-7 py-3 rounded-xl bg-finance-accent text-black text-sm font-black uppercase tracking-widest hover:bg-yellow-500 transition-all shadow-lg shadow-finance-accent/10">Get Started</Link>
            </div>

            {/* Mobile Hamburger Toggle */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="Toggle Menu"
                className="p-2 text-gray-300 hover:text-white bg-gray-800/40 hover:bg-gray-800 rounded-lg transition-all border border-gray-700/50"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </>
        ) : (


          /* ── LOGGED-IN NAVIGATION (Same as before) ── */
          <div className="flex items-center space-x-4">
            {profile && (profile.tier === 'Pro' || profile.tier === 'Elite') && (
              <div className="hidden sm:flex flex-col items-end mr-2">
                <span className="text-[11px] text-gray-500 uppercase font-bold tracking-widest mb-0.5">Monthly Quota</span>
                <div className="text-[11px] text-finance-accent font-mono bg-gray-900 border border-gray-700 px-2 py-0.5 rounded shadow-inner flex gap-2">
                  <span>Used: <span className="text-white">{profile.monthly_checks_used}</span></span>
                  <span className="text-gray-600">|</span>
                  <span>Left: <span className="text-emerald-400">{(PLAN_QUOTA_LIMITS[profile.tier] ?? 250) - profile.monthly_checks_used}</span></span>
                </div>
              </div>
            )}

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle Menu"
              className="p-2 text-gray-300 hover:text-white bg-gray-800/40 hover:bg-gray-800 rounded-lg transition-all border border-gray-700/50"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        )}
      </div>

      {/* Slide-out Menu (Mobile for Guests, Global for Logged-In) */}
      {isMobileMenuOpen && (
        <div className="absolute top-16 right-0 w-80 bg-[#000000] border-l border-b border-gray-800 shadow-2xl z-50 animate-in slide-in-from-top-2 duration-300">
          <div className="px-6 py-8 space-y-6 max-h-[85vh] overflow-y-auto custom-scrollbar">

            {/* User Profile Hook (If Logged In) / Account Access (If Guest) */}
            {session ? (
              <div className="pb-6 border-b border-gray-800">
                <div className="flex items-center gap-4 bg-gray-900/50 p-4 rounded-2xl border border-gray-800">
                  <div className="w-12 h-12 rounded-full bg-finance-accent/10 border border-finance-accent/30 flex items-center justify-center text-finance-accent font-black text-xl">
                    {session?.user?.email?.charAt(0).toUpperCase()}
                  </div>
                  <div className="overflow-hidden">
                    <div className="text-sm text-white font-bold truncate">{session?.user?.email}</div>
                    <div className="text-[11px] font-black text-finance-accent uppercase tracking-widest">{profile?.tier || 'Free'} Member</div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* ── GUEST MOBILE NAVIGATION ── */}
                <div className="space-y-4">
                  <p className="text-[11px] font-black text-gray-500 uppercase tracking-widest mb-4 ml-1">Explore Platform</p>

                  {/* 1. Get Started */}
                  <Link
                    to="/pricing"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="w-full flex items-center justify-center py-3.5 px-4 rounded-xl bg-finance-accent text-finance-darker font-black uppercase tracking-widest text-xs hover:bg-yellow-500 transition-all shadow-lg shadow-finance-accent/10"
                  >
                    Get Started
                  </Link>

                  {/* 2. Sign In */}
                  <Link
                    to="/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="w-full flex items-center justify-center py-3.5 px-4 rounded-xl border border-gray-700 text-gray-400 font-black uppercase tracking-widest text-xs hover:bg-gray-800 hover:text-white transition-all"
                  >
                    Sign In
                  </Link>

                  <div className="h-px bg-gray-800 w-full my-4" />

                  {/* 3. Features Accordion */}
                  <div className="space-y-1">
                    <button
                      onClick={() => { setFeaturesOpen(!featuresOpen); setAnalysisOpen(false); setSignalsOpen(false); }}
                      className="w-full flex items-center justify-between px-4 py-4 rounded-xl bg-white/[0.03] border border-white/5 text-finance-accent font-black tracking-widest uppercase text-[11px]"
                    >
                      <span>Platform Features</span>
                      <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${featuresOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {featuresOpen && (
                      <div className="pl-4 pt-2 space-y-2 border-l border-gray-800 ml-6">
                        <button onClick={() => setAnalysisOpen(!analysisOpen)} className="w-full flex items-center justify-between p-3 rounded-lg text-gray-300 font-bold hover:bg-white/5 text-xs">
                          <span>Analysis Tools</span>
                          <ChevronDown className={`w-3 h-3 transition-transform ${analysisOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {analysisOpen && (
                          <div className="pl-4 space-y-1">
                            <Link to="/features/fundamental" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 text-xs text-gray-400 hover:text-white font-bold transition-colors">Fundamental Analysis</Link>
                            <Link to="/features/cot" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 text-xs text-gray-400 hover:text-white font-bold transition-colors">COT Reports</Link>
                            <Link to="/features/order-flow" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 text-xs text-gray-400 hover:text-white font-bold transition-colors">Order Flow mapping</Link>
                            <Link to="/features/charts" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 text-xs text-gray-400 hover:text-white font-bold transition-colors">Chart Insight</Link>
                          </div>
                        )}

                        <button onClick={() => setSignalsOpen(!signalsOpen)} className="w-full flex items-center justify-between p-3 rounded-lg text-gray-300 font-bold hover:bg-white/5 text-xs">
                          <span>Trading Signals</span>
                          <ChevronDown className={`w-3 h-3 transition-transform ${signalsOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {signalsOpen && (
                          <div className="pl-4 space-y-1">
                            <Link to="/features/standard-signals" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 text-xs text-gray-400 hover:text-white font-bold transition-colors">Standard Tier</Link>
                            <Link to="/features/elite-signals" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 text-xs text-gray-400 hover:text-white font-bold transition-colors">Elite Tier</Link>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* 4. Pricing */}
                  <Link
                    to="/pricing"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center px-4 py-3 rounded-xl text-gray-400 font-bold hover:text-white hover:bg-white/5 transition-all text-sm"
                  >
                    Pricing Plans
                  </Link>

                  {/* 5. Insights */}
                  <Link
                    to="/insights"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center px-4 py-3 rounded-xl text-gray-400 font-bold hover:text-white hover:bg-white/5 transition-all text-sm"
                  >
                    Market Insights
                  </Link>

                  {/* 6. About */}
                  <Link
                    to="/about"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center px-4 py-3 rounded-xl text-gray-400 font-bold hover:text-white hover:bg-white/5 transition-all text-sm"
                  >
                    About Company
                  </Link>

                  {/* 7. Contact */}
                  <Link
                    to="/support"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center px-4 py-3 rounded-xl text-gray-400 font-bold hover:text-white hover:bg-white/5 transition-all text-sm"
                  >
                    Contact Support
                  </Link>
                </div>
              </>
            )}


            {/* Member Links (Only if Logged In) */}
            {session && (
              <div className="space-y-4">
                <div className="space-y-1">
                  <p className="text-[11px] font-black text-gray-500 uppercase tracking-widest mb-4 ml-1">Member Dashboard</p>

                  {['Signals', 'Premium', 'Elite'].includes(profile?.tier || '') && (
                    <Link to="/signals" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-4 rounded-xl text-emerald-400 font-bold bg-emerald-400/5 border border-emerald-400/10">
                      <TrendingUp className="w-4 h-4" /> Institutional AI Signals
                    </Link>
                  )}

                  {['Signals', 'Premium', 'Elite'].includes(profile?.tier || '') && (
                    <Link to="/signal-history" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-finance-accent font-bold bg-finance-accent/5 border border-finance-accent/10">
                      <HistoryIcon className="w-4 h-4" /> Signal History
                    </Link>
                  )}

                  <Link to="/dashboard" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-4 rounded-xl text-finance-accent font-black bg-finance-accent/5 border border-finance-accent/10">
                    <Brain className="w-4 h-4" /> Institutional Insight
                  </Link>

                  {profile?.tier === 'Elite' && (
                    <Link to="/elite-signals" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-yellow-400 font-black bg-yellow-400/5 border border-yellow-400/10">
                      <Zap className="w-4 h-4" /> Elite Signal
                    </Link>
                  )}


                  <Link to="/settings" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-300 font-bold hover:bg-white/5 transition-colors">
                    <Settings className="w-4 h-4" /> Autotrade Engine
                  </Link>

                  <Link to="/profile" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-300 font-bold hover:bg-white/5 transition-colors">
                    <ShieldAlert className="w-4 h-4 text-finance-accent" /> My Account
                  </Link>
                </div>

                {/* Shared Links for Members Only */}
                <div className="pt-6 border-t border-gray-800">
                  <p className="text-[11px] font-black text-gray-600 uppercase tracking-[3px] mb-4 ml-1">Company</p>
                  <div className="space-y-1">
                    <Link to="/pricing" onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-2 text-sm text-gray-400 hover:text-white font-bold transition-colors">Pricing Plans</Link>
                    <Link to="/insights" onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-2 text-sm text-gray-400 hover:text-white font-bold transition-colors">Insights & News</Link>
                    <Link to="/about" onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-2 text-sm text-gray-400 hover:text-white font-bold transition-colors">Our Philosophy</Link>
                  </div>
                </div>
              </div>
            )}





            {/* Admin Console */}
            {(profile?.role === 'admin' || profile?.is_admin) && (
              <Link to="/admin" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center justify-center gap-2 py-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-500 font-black uppercase tracking-widest text-[11px]">
                <ShieldAlert className="w-3.5 h-3.5" /> Admin Console
              </Link>
            )}

            {/* Sign Out */}
            {session && (
              <button
                onClick={async () => { await supabase.auth.signOut(); setIsMobileMenuOpen(false); navigate('/'); }}
                className="w-full flex items-center justify-center gap-2 py-4 mt-4 bg-red-500 border border-red-600 text-finance-darker font-black rounded-xl hover:bg-red-600 transition-all uppercase tracking-widest text-xs"
              >
                <LogOut className="w-4 h-4" /> Sign Out System
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
};